import React from 'react';

// KPI Card Skeleton
export function KPICardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
        <div className="w-16 h-4 bg-gray-200 rounded"></div>
      </div>
      <div className="space-y-2">
        <div className="w-20 h-8 bg-gray-200 rounded"></div>
        <div className="w-24 h-4 bg-gray-200 rounded"></div>
        <div className="w-32 h-3 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
}

// Chart Skeleton
export function ChartSkeleton({ height = 'h-48' }: { height?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
      <div className="flex items-center justify-between mb-6">
        <div className="w-32 h-6 bg-gray-200 rounded"></div>
        <div className="w-20 h-4 bg-gray-200 rounded"></div>
      </div>
      <div className={`${height} bg-gray-100 rounded-lg mb-4`}></div>
      <div className="flex justify-between">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="w-8 h-4 bg-gray-200 rounded"></div>
        ))}
      </div>
    </div>
  );
}

// Table Skeleton
export function TableSkeleton() {
  return (
    <div className="table-container animate-pulse">
      <div className="page-header">
        <div>
          <div className="w-32 h-6 bg-gray-200 rounded mb-2"></div>
          <div className="w-48 h-4 bg-gray-200 rounded"></div>
        </div>
        <div className="w-24 h-8 bg-gray-200 rounded"></div>
      </div>
      
      <div className="table-wrapper">
        <table className="transactions-table">
          <thead className="table-header">
            <tr>
              {Array.from({ length: 8 }).map((_, i) => (
                <th key={i} className="px-6 py-3">
                  <div className="w-16 h-4 bg-gray-200 rounded"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 4 }).map((_, rowIndex) => (
              <tr key={rowIndex} className="table-row">
                {Array.from({ length: 8 }).map((_, colIndex) => (
                  <td key={colIndex} className="px-6 py-4">
                    {colIndex === 0 ? (
                      <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    ) : colIndex === 1 ? (
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded mr-3"></div>
                        <div>
                          <div className="w-20 h-4 bg-gray-200 rounded mb-1"></div>
                          <div className="w-16 h-3 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-16 h-4 bg-gray-200 rounded"></div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Compliance Card Skeleton
export function ComplianceCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
      <div className="flex items-center justify-between mb-6">
        <div className="w-40 h-6 bg-gray-200 rounded"></div>
        <div className="w-5 h-5 bg-gray-200 rounded"></div>
      </div>
      
      <div className="space-y-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="w-24 h-4 bg-gray-200 rounded"></div>
            <div className="w-8 h-6 bg-gray-200 rounded"></div>
          </div>
          <div className="w-32 h-3 bg-gray-200 rounded"></div>
        </div>
        
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="w-28 h-4 bg-gray-200 rounded"></div>
            <div className="w-6 h-6 bg-gray-200 rounded"></div>
          </div>
          <div className="w-36 h-3 bg-gray-200 rounded"></div>
        </div>
        
        <div className="w-full h-10 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  );
}

// Full Dashboard Skeleton
export function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header Skeleton */}
      <div className="bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="w-48 h-8 bg-gray-200 rounded mb-2 animate-pulse"></div>
            <div className="w-64 h-4 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-48 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="w-32 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
            <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Main Content Skeleton */}
      <div className="p-6 space-y-6 bg-white">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <KPICardSkeleton key={i} />
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <ChartSkeleton />
          <ChartSkeleton />
          <ComplianceCardSkeleton />
        </div>

        {/* Table */}
        <TableSkeleton />
      </div>
    </div>
  );
}
